// @ts-nocheck
import React, {FC, Fragment, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {
    Badge,
    Box,
    Checkbox,
    Divider,
    Flex,
    Grid,
    HStack,
    IconButton,
    Input,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Text,
    Tooltip,
    useBreakpointValue,
    useToast,
} from "@chakra-ui/react";
import type {ReactNode} from "react";
import {CloseIcon} from "@chakra-ui/icons";
import {FiEye, FiEyeOff, FiSend, FiSettings, FiSlash} from "react-icons/fi";
import {Breadcrumbs} from "../../components/Breadcrumbs/Breadcrumbs";
import {Loader} from "../../components/Loader/Loader";
import {Paginator} from "../../components/Paignator/Paginator";
import {setBreadcrumbsLinks} from "../../redux/Breadcrumbs/BreadcrumbsReducer";
import {BreadcrumbsData} from "../../shared/entities/Breadcrumbs/BreadCrumbsData";
import {InactiveOrderReminderController} from "../../controllers/InactiveOrderReminderController";
import {
    InactiveDealershipItem,
    InactiveOrderReminderHiddenFilter,
    InactiveProductItem,
} from "../../shared/entities/Order/InactiveOrderReminder";
import AllowedAccess, {hasPermission} from "../../features/Permission/AllowedAccess";
import {
    inactiveOrderReminderSendPermission,
    inactiveOrderReminderViewPermission,
} from "../permissions";
import InactiveOrderReminderRecipientsModal from "../Modal/InactiveOrderReminderRecipientsModal";
import InactiveOrderReminderConfirmSendModal from "../Modal/InactiveOrderReminderConfirmSendModal";
import ShowOrderModal from "../Modal/ShowOrderModal";
import {Order, OrderTypes} from "../../shared/entities/Order/Order";
import ImageCustom from "../../components/Image/ImageCustom";
import IconWithTooltip from "../../components/Icon/IconWithTooltip";
import {Product} from "../../shared/entities/Product/Product";
import CustomLink from "../../components/Link/CustomLink";
import {WebPUtils} from "../../shared/utilities/WebPUtils";
import {useAppSelector} from "../../redux/Store";
import {DealershipMinInfo} from "../../shared/entities/Users/Dealership";

const FALLBACK_MONTH_THRESHOLDS = [1, 3, 6, 9, 12];

const monthThresholdLabel = (months: number, compact = false) =>
    compact
        ? (months === 1 ? "1 month" : `${months} months`)
        : (months === 1 ? "≥ 1 month" : `≥ ${months} months`);

const HIDDEN_FILTER_OPTIONS: {value: InactiveOrderReminderHiddenFilter; label: string}[] = [
    {value: "active", label: "Active only"},
    {value: "all", label: "All dealerships"},
    {value: "manual", label: "Manual exclusions"},
    {value: "send", label: "Send suppressions"},
];

/** Единая сетка для header и всех строк — как desktop grid в Order History */
const GRID_TEMPLATE = "40px 56px minmax(200px, 1fr) 200px 90px 150px 110px";
const GRID_CELL_SX = {
    "& > *": {
        px: 2,
        display: "flex",
        alignItems: "center",
        minWidth: 0,
        overflow: "hidden",
    },
    "& > *:not(:last-child)": {
        borderRight: "1px solid",
        borderColor: "gray.100",
    },
};

const InactiveReminderGridRow = ({
    cells,
    variant = "dealer",
    noBorder = false,
}: {
    cells: ReactNode[];
    variant?: "header" | "dealer" | "product";
    noBorder?: boolean;
}) => {
    const isHeader = variant === "header";
    const isProduct = variant === "product";
    return (
        <Grid
            templateColumns={GRID_TEMPLATE}
            w="100%"
            alignItems="center"
            columnGap={0}
            px={2}
            py={isHeader ? 2 : 0}
            minH="35px"
            fontSize="13px"
            fontWeight={isHeader ? 700 : undefined}
            bg={isHeader ? "aliceblue" : isProduct ? "white" : undefined}
            borderBottom={noBorder ? "none" : "1px solid #71809640"}
            sx={{
                ...GRID_CELL_SX,
                ...(isHeader
                    ? {"& > *:not(:last-child)": {borderRight: "1px solid", borderColor: "gray.200"}}
                    : {}),
            }}
        >
            {cells.map((cell, idx) => (
                <Box
                    key={idx}
                    justifyContent={idx === cells.length - 1 ? "flex-end" : "flex-start"}
                >
                    {cell}
                </Box>
            ))}
        </Grid>
    );
};

const orderStubForPopover = (id: number): Order => ({
    id,
    tax: 0,
    shippingCost: 0,
    totalAmount: 0,
    sendCount: 0,
    paymentStatus: OrderTypes.PENDING,
    products: [],
} as Order);

const INACTIVE_TIER_BUCKETS = [1, 3, 6, 9, 12] as const;

const lastOrderTooltipLabel = (p: InactiveProductItem) =>
    [
        p.lastOrderId != null ? `Order #${p.lastOrderId}` : null,
        p.productName ? `Product: ${p.productName}` : null,
        p.lastOrderDate ? `Last order: ${p.lastOrderDate}` : null,
        p.inactiveMonths != null ? `Inactive: ${p.inactiveMonths} mo (${tierLabel(p.inactiveTier)})` : null,
    ].filter(Boolean).join("\n");

const lastReminderTooltip = (date: string) => `Last reminder: ${date}`;

const lastOrderDateCell = (p: InactiveProductItem) => (
    <Tooltip key="date" label={lastOrderTooltipLabel(p)} placement="top" hasArrow>
        <Text fontSize="13px" color="gray.600" isTruncated maxW="100%" cursor="default">
            {p.lastOrderDate}
        </Text>
    </Tooltip>
);

const dealerLastReminderCell = (dealer: InactiveDealershipItem) =>
    dealer.lastReminderSentDate ? (
        <Tooltip key="last" label={lastReminderTooltip(dealer.lastReminderSentDate)} placement="top" hasArrow>
            <Text fontSize="11px" color="gray.400" isTruncated maxW="100%" cursor="default">
                {dealer.lastReminderSentDate}
            </Text>
        </Tooltip>
    ) : (
        <Box key="last"/>
    );

const tierColor = (tier: number) => {
    if (tier >= 12) return "purple";
    if (tier >= 9) return "red";
    if (tier >= 6) return "orange";
    if (tier >= 3) return "yellow";
    return "gray";
};

const tierLabel = (tier: number) => `${tier} mo`;

const hasSendableProducts = (dealer: InactiveDealershipItem) =>
    dealer.products?.some((p) => !p.reminderExcluded) ?? false;

const sendReminderTooltip = (dealers: InactiveDealershipItem[]) => {
    if (dealers.length === 0) {
        return "Select at least one dealership";
    }
    if (!dealers.some(hasSendableProducts)) {
        return "No active products";
    }
    if (dealers.length === 1 && !dealers[0].recipientsConfigured) {
        return "Configure emails (settings), then send";
    }
    if (dealers.length > 1) {
        return `Send reminder (${dealers.length} selected)`;
    }
    return "Send reminder";
};

const toProductImageElem = (p: InactiveProductItem): Product => ({
    id: p.productId,
    name: p.productName,
    url: p.url || String(p.productId),
    primaryImageUuid: p.primaryImageUuid || "",
    images: p.primaryImageUuid ? [{uuid: p.primaryImageUuid}] : [],
} as Product);

export const InactiveOrderReminder: FC<{ links: Array<BreadcrumbsData> }> = ({links}) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const controller = useMemo(() => new InactiveOrderReminderController(dispatch), [dispatch]);
    const monthsThresholdInitialized = useRef(false);

    useEffect(() => {
        setBreadcrumbsLinks(dispatch, links);
    }, [dispatch, links]);
    const dealershipsMinInfo = useAppSelector((state) => state.UserReducer.dealershipsMinInfo);
    const user = useAppSelector((state) => state.AuthReducer.user);

    const [isLoading, setIsLoading] = useState(false);
    const [list, setList] = useState<{ items: InactiveDealershipItem[]; count: number }>({items: [], count: 0});
    const [allowedMonthThresholds, setAllowedMonthThresholds] = useState<number[]>(FALLBACK_MONTH_THRESHOLDS);
    const [monthsThreshold, setMonthsThreshold] = useState<string | null>(null);
    const [pendingCount, setPendingCount] = useState<number | null>(null);
    const [dealershipName, setDealershipName] = useState("");
    const [searchName, setSearchName] = useState("");
    const [isDealershipDropdownOpen, setIsDealershipDropdownOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hiddenFilter, setHiddenFilter] = useState<InactiveOrderReminderHiddenFilter>("active");
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

    const [settingsOpen, setSettingsOpen] = useState(false);
    const [settingsDealers, setSettingsDealers] = useState<InactiveDealershipItem[]>([]);
    const [confirmSendOpen, setConfirmSendOpen] = useState(false);
    const [sendDealers, setSendDealers] = useState<InactiveDealershipItem[]>([]);
    const isMobile = useBreakpointValue({base: true, sm: true, md: false, lg: false});

    const monthOptions = useMemo(
        () =>
            allowedMonthThresholds.map((value) => ({
                value: String(value),
                label: monthThresholdLabel(value, !!isMobile),
            })),
        [allowedMonthThresholds, isMobile],
    );

    const loadPendingCount = useCallback(() => {
        const params: Record<string, unknown> = {
            dealershipName: searchName || undefined,
        };
        if (monthsThreshold != null) {
            params.monthsThreshold = Number(monthsThreshold);
        }
        return controller.pendingCount(params).then((res) => {
            if (!res) {
                return;
            }
            if (!monthsThresholdInitialized.current) {
                monthsThresholdInitialized.current = true;
                setMonthsThreshold(String(res.monthsThreshold));
            }
            if (res.allowedMonthsThresholds?.length) {
                setAllowedMonthThresholds((prev) => {
                    const next = res.allowedMonthsThresholds;
                    if (prev.length === next.length && prev.every((v, i) => v === next[i])) {
                        return prev;
                    }
                    return next;
                });
            }
            setPendingCount(res.count);
        });
    }, [controller, monthsThreshold, searchName]);

    const loadList = useCallback(() => {
        if (monthsThreshold === null) {
            return;
        }
        setIsLoading(true);
        const page = Math.max(0, currentPage - 1);
        controller
            .list({
                monthsThreshold: Number(monthsThreshold),
                dealershipName: searchName || undefined,
                hiddenFilter,
                page,
                size: 20,
            })
            .then((res) => {
                setList(res || {items: [], count: 0});
            })
            .finally(() => setIsLoading(false));
    }, [controller, monthsThreshold, searchName, hiddenFilter, currentPage]);

    useEffect(() => {
        if (!hasPermission(inactiveOrderReminderViewPermission)) {
            return;
        }
        loadPendingCount();
    }, [loadPendingCount]);

    useEffect(() => {
        if (!hasPermission(inactiveOrderReminderViewPermission) || monthsThreshold === null) {
            return;
        }
        loadList();
    }, [loadList]);

    useEffect(() => {
        setSelectedIds(new Set());
        setCurrentPage(1);
    }, [monthsThreshold, searchName, hiddenFilter]);

    const selectedDealerships = useMemo(
        () => list.items.filter((d) => selectedIds.has(d.dealershipId) && !d.hidden),
        [list.items, selectedIds],
    );
    const selectedDealershipsWithSendableProducts = useMemo(
        () => selectedDealerships.filter(hasSendableProducts),
        [selectedDealerships],
    );
    const dealershipSuggestions = useMemo<string[]>(() => {
        const fromMinInfo = (dealershipsMinInfo || []).map((d: DealershipMinInfo) => d.name);
        const fromUser = (user?.dealerships || []).map((d) => d.name);
        return Array.from(new Set([...fromMinInfo, ...fromUser].filter(Boolean))).sort((a, b) => a.localeCompare(b));
    }, [dealershipsMinInfo, user?.dealerships]);
    const filteredDealershipSuggestions = useMemo(() => {
        const query = dealershipName.trim().toLowerCase();
        const source = dealershipSuggestions.filter(Boolean);
        if (!query) {
            return source.slice(0, 50);
        }
        return source.filter((name) => name.toLowerCase().includes(query)).slice(0, 50);
    }, [dealershipSuggestions, dealershipName]);

    const openSettings = (dealers: InactiveDealershipItem[]) => {
        if (dealers.length === 0) {
            toast({title: "Select at least one dealership", status: "warning", duration: 3000, isClosable: true});
            return;
        }
        setSettingsDealers(dealers);
        setSettingsOpen(true);
    };

    const openSendConfirm = (dealers: InactiveDealershipItem[]) => {
        const eligible = dealers.filter(hasSendableProducts);
        if (eligible.length === 0) {
            toast({
                title: dealers.length === 0
                    ? "Select at least one dealership"
                    : "No active products",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        setSendDealers(eligible);
        setConfirmSendOpen(true);
    };

    const excludeDealership = async (dealer: InactiveDealershipItem) => {
        const result = await controller.setDealershipExcluded({
            dealershipId: dealer.dealershipId,
            excluded: true,
        });
        if (result?.None) {
            toast({title: "Failed to exclude dealership", status: "error", duration: 4000, isClosable: true});
            return;
        }
        toast({
            title: `${dealer.dealershipName || "Dealership"} excluded from reminders`,
            status: "success",
            duration: 3000,
            isClosable: true,
        });
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(dealer.dealershipId);
            return next;
        });
        loadList();
    };

    const includeDealership = async (dealer: InactiveDealershipItem) => {
        const result = await controller.setDealershipExcluded({
            dealershipId: dealer.dealershipId,
            excluded: false,
        });
        if (result?.None) {
            toast({title: "Failed to include dealership", status: "error", duration: 4000, isClosable: true});
            return;
        }
        toast({
            title: `${dealer.dealershipName || "Dealership"} included in reminders`,
            status: "success",
            duration: 3000,
            isClosable: true,
        });
        loadList();
    };

    const toggleProductExcluded = async (
        dealer: InactiveDealershipItem,
        product: InactiveProductItem,
        excluded: boolean,
    ) => {
        const result = await controller.setProductExcluded({
            dealershipId: dealer.dealershipId,
            productId: product.productId,
            excluded,
        });
        if (result?.None) {
            toast({
                title: excluded ? "Failed to exclude product" : "Failed to include product",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        toast({
            title: excluded ? "Product excluded from reminders" : "Product included in reminders",
            status: "success",
            duration: 3000,
            isClosable: true,
        });
        loadList();
    };

    const renderActionIcons = (dealers: InactiveDealershipItem[]) => (
        <HStack spacing={1} justify="flex-end" w="100%">
            <IconWithTooltip
                icon={FiSlash}
                fontSize="0.9rem"
                tooltipMessage="Exclude dealership from reminders"
                onClick={() => excludeDealership(dealers[0])}
            />
            <IconWithTooltip
                icon={FiSettings}
                fontSize="0.9rem"
                ml={0}
                tooltipMessage="Email recipients settings"
                onClick={() => openSettings(dealers)}
            />
            <IconWithTooltip
                icon={FiSend}
                fontSize="0.9rem"
                tooltipMessage={sendReminderTooltip(dealers)}
                isDisabled={!hasSendableProducts(dealers[0])}
                onClick={() => openSendConfirm(dealers)}
            />
        </HStack>
    );

    const toggleSelect = (id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleExpand = (id: number) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const productNameCell = (p: InactiveProductItem) => {
        const productElem = toProductImageElem(p);
        const productLink = p.url ? WebPUtils.getProductLink(productElem) : undefined;
        return productLink ? (
            <Tooltip label={p.productName} placement="top">
                <Text fontSize="13px" isTruncated maxW="100%">
                    <CustomLink href={productLink}>{p.productName}</CustomLink>
                </Text>
            </Tooltip>
        ) : (
            <Tooltip label={p.productName} placement="top">
                <Text fontSize="13px" isTruncated maxW="100%">{p.productName}</Text>
            </Tooltip>
        );
    };

    const productSubRow = (dealer: InactiveDealershipItem, p: InactiveProductItem) => {
        const productElem = toProductImageElem(p);
        const isExcluded = !!p.reminderExcluded;
        return (
            <Box
                key={p.productId}
                opacity={isExcluded ? 0.55 : 1}
                bg={isExcluded ? "gray.50" : "white"}
            >
                <InactiveReminderGridRow
                    variant="product"
                    noBorder
                    cells={[
                        <Box key="cb"/>,
                        <ImageCustom
                            key="img"
                            elem={productElem}
                            sizeInPercent={100}
                            currentSize="small"
                            sizeBackend="COMMON"
                            showOnHover={false}
                        />,
                        <Flex key="name" align="center" gap={1} minW={0} w="100%">
                            {isExcluded && (
                                <Tooltip label="Excluded from reminders" placement="top">
                                    <Box as="span" color="orange.400" lineHeight={0} flexShrink={0}>
                                        <FiEyeOff size={14}/>
                                    </Box>
                                </Tooltip>
                            )}
                            <Box minW={0} flex={1}>
                                {productNameCell(p)}
                            </Box>
                        </Flex>,
                        <Badge
                            key="tier"
                            colorScheme={isExcluded ? "gray" : tierColor(p.inactiveTier)}
                            fontSize="12px"
                        >
                            {isExcluded ? "excluded" : tierLabel(p.inactiveTier)}
                        </Badge>,
                        p.lastOrderId ? (
                            <ShowOrderModal key="ord" order={orderStubForPopover(p.lastOrderId)} openTrigger="hover"/>
                        ) : (
                            <Text key="ord-empty" fontSize="13px" color="gray.500">—</Text>
                        ),
                        lastOrderDateCell(p),
                        hasPermission(inactiveOrderReminderSendPermission) ? (
                            <IconWithTooltip
                                key="actions"
                                icon={isExcluded ? FiEye : FiEyeOff}
                                fontSize="0.9rem"
                                color={isExcluded ? "green.500" : undefined}
                                tooltipMessage={
                                    isExcluded
                                        ? "Include product in reminders"
                                        : "Exclude product from reminders"
                                }
                                onClick={() => toggleProductExcluded(dealer, p, !isExcluded)}
                            />
                        ) : (
                            <Box key="actions"/>
                        ),
                    ]}
                />
            </Box>
        );
    };

    const dealerRows = list.items.map((dealer) => {
        const isHiddenDealer = !!dealer.hidden;
        const isSelected = selectedIds.has(dealer.dealershipId);
        const isExpanded = expandedIds.has(dealer.dealershipId);
        const configured = !!dealer.recipientsConfigured;
        const hiddenLabel = dealer.hiddenReason === "MANUAL"
            ? "exclude manual"
            : dealer.hiddenUntilDate
                ? `exclude until ${dealer.hiddenUntilDate}`
                : "exclude";

        return (
            <Fragment key={dealer.dealershipId}>
                <Box opacity={isHiddenDealer ? 0.6 : 1} bg={isHiddenDealer ? "gray.50" : "transparent"}>
                    <InactiveReminderGridRow
                        cells={[
                        <Checkbox
                            key="cb"
                            size="sm"
                            isChecked={isSelected}
                            isDisabled={isHiddenDealer}
                            onChange={() => toggleSelect(dealer.dealershipId)}
                            onClick={(e) => e.stopPropagation()}
                        />,
                        <Box key="img"/>,
                        <Tooltip
                            key="name"
                            label={dealer.dealershipName || `Dealership #${dealer.dealershipId}`}
                            placement="top"
                        >
                            <Text
                                fontSize="13px"
                                fontWeight="600"
                                cursor="pointer"
                                isTruncated
                                maxW="100%"
                                onClick={() => toggleExpand(dealer.dealershipId)}
                            >
                                {dealer.dealershipName || `Dealership #${dealer.dealershipId}`}
                                {isHiddenDealer && (
                                    <Badge ml={2} colorScheme="gray" variant="subtle" fontSize="10px">
                                        {hiddenLabel}
                                    </Badge>
                                )}
                                <Text as="span" color="gray.500" fontWeight="normal" ml={2}>
                                    ({dealer.products?.length || 0} products)
                                </Text>
                            </Text>
                        </Tooltip>,
                        <HStack key="inactive" spacing={1} flexWrap="wrap">
                            {isHiddenDealer && (
                                <Badge colorScheme="gray" variant="solid" fontSize="10px">
                                    {hiddenLabel}
                                </Badge>
                            )}
                            {INACTIVE_TIER_BUCKETS.map((t) => {
                                const count = dealer.products?.filter((p) => p.inactiveTier === t).length || 0;
                                if (!count) return null;
                                return (
                                    <Badge key={t} colorScheme={tierColor(t)} variant="subtle" fontSize="11px">
                                        {t} mo: {count}
                                    </Badge>
                                );
                            })}
                        </HStack>,
                        <Box key="order"/>,
                        dealerLastReminderCell(dealer),
                        hasPermission(inactiveOrderReminderSendPermission) && !isHiddenDealer ? (
                            <Box key="actions" position="relative" w="100%">
                                {configured && (
                                    <Box
                                        position="absolute"
                                        top="-2px"
                                        right="-2px"
                                        w="6px"
                                        h="6px"
                                        borderRadius="full"
                                        bg="green.400"
                                        zIndex={1}
                                    />
                                )}
                                {renderActionIcons([dealer])}
                            </Box>
                        ) : hasPermission(inactiveOrderReminderSendPermission)
                        && isHiddenDealer
                        && dealer.hiddenReason === "MANUAL" ? (
                            <HStack key="actions" spacing={0} justify="flex-end" w="100%">
                                <IconWithTooltip
                                    icon={FiEye}
                                    fontSize="0.9rem"
                                    color="green.500"
                                    tooltipMessage="Include dealership in reminders"
                                    onClick={() => includeDealership(dealer)}
                                />
                            </HStack>
                        ) : (
                            <Box key="actions"/>
                        ),
                        ]}
                        variant={isHiddenDealer ? "product" : "dealer"}
                    />
                </Box>
                {isExpanded && dealer.products?.map((p) => productSubRow(dealer, p))}
            </Fragment>
        );
    });

    const mobileProductRow = (dealer: InactiveDealershipItem, p: InactiveProductItem) => {
        const isExcluded = !!p.reminderExcluded;
        const productElem = toProductImageElem(p);
        return (
            <Flex
                key={p.productId}
                p={2}
                borderWidth="1px"
                borderRadius="md"
                bg={isExcluded ? "gray.50" : "white"}
                opacity={isExcluded ? 0.65 : 1}
                alignItems="flex-start"
                gap={2}
            >
                <Box minW="42px">
                    <ImageCustom
                        elem={productElem}
                        sizeInPercent={100}
                        currentSize="small"
                        sizeBackend="COMMON"
                        showOnHover={false}
                    />
                </Box>
                <Box flex={1} minW={0}>
                    <Flex align="center" gap={1} mb={1}>
                        {isExcluded && (
                            <Tooltip label="Excluded from reminders" placement="top">
                                <Box as="span" color="orange.400" lineHeight={0}>
                                    <FiEyeOff size={14}/>
                                </Box>
                            </Tooltip>
                        )}
                        <Box minW={0} flex={1}>
                            {productNameCell(p)}
                        </Box>
                    </Flex>
                    <HStack spacing={1} flexWrap="wrap" align="flex-start">
                        <Badge colorScheme={isExcluded ? "gray" : tierColor(p.inactiveTier)} fontSize="11px">
                            {isExcluded ? "excluded" : tierLabel(p.inactiveTier)}
                        </Badge>
                        <Text fontSize="12px" color="gray.600">{p.lastOrderDate}</Text>
                    </HStack>
                </Box>
                <Box>
                    {hasPermission(inactiveOrderReminderSendPermission) ? (
                        <IconWithTooltip
                            icon={isExcluded ? FiEye : FiEyeOff}
                            fontSize="0.9rem"
                            color={isExcluded ? "green.500" : undefined}
                            tooltipMessage={isExcluded ? "Include product in reminders" : "Exclude product from reminders"}
                            onClick={() => toggleProductExcluded(dealer, p, !isExcluded)}
                        />
                    ) : null}
                </Box>
            </Flex>
        );
    };

    const mobileDealerCards = list.items.map((dealer) => {
        const isHiddenDealer = !!dealer.hidden;
        const isSelected = selectedIds.has(dealer.dealershipId);
        const isExpanded = expandedIds.has(dealer.dealershipId);
        const configured = !!dealer.recipientsConfigured;
        const hiddenLabel = dealer.hiddenReason === "MANUAL"
            ? "exclude manual"
            : dealer.hiddenUntilDate
                ? `exclude until ${dealer.hiddenUntilDate}`
                : "exclude";

        return (
            <Box
                key={dealer.dealershipId}
                borderWidth="1px"
                borderRadius="md"
                p={3}
                mb={2}
                bg={isHiddenDealer ? "gray.50" : "white"}
                opacity={isHiddenDealer ? 0.7 : 1}
            >
                <Flex justify="space-between" align="flex-start" gap={2}>
                    <Flex align="flex-start" gap={3} flex={1} minW={0}>
                        <Box mt="2px" mr={1.5} flexShrink={0}>
                            <Checkbox
                                size="sm"
                                isChecked={isSelected}
                                isDisabled={isHiddenDealer}
                                onChange={() => toggleSelect(dealer.dealershipId)}
                            />
                        </Box>
                        <Box minW={0} flex={1}>
                            <Text fontSize="13px" fontWeight="600" isTruncated>
                                {dealer.dealershipName || `Dealership #${dealer.dealershipId}`}
                            </Text>
                            <HStack spacing={1} mt={1} flexWrap="wrap">
                                {isHiddenDealer && (
                                    <Badge colorScheme="gray" fontSize="10px">{hiddenLabel}</Badge>
                                )}
                                {INACTIVE_TIER_BUCKETS.map((t) => {
                                    const count = dealer.products?.filter((p) => p.inactiveTier === t).length || 0;
                                    if (!count) return null;
                                    return (
                                        <Badge key={t} colorScheme={tierColor(t)} variant="subtle" fontSize="10px">
                                            {t} mo: {count}
                                        </Badge>
                                    );
                                })}
                            </HStack>
                            {dealer.lastReminderSentDate ? (
                                <Tooltip label={lastReminderTooltip(dealer.lastReminderSentDate)} placement="top" hasArrow>
                                    <Text fontSize="11px" color="gray.400" mt={1} cursor="default">
                                        {dealer.lastReminderSentDate}
                                    </Text>
                                </Tooltip>
                            ) : null}
                        </Box>
                    </Flex>
                    {hasPermission(inactiveOrderReminderSendPermission) && !isHiddenDealer ? (
                        <HStack spacing={0}>
                            {configured && <Box w="6px" h="6px" borderRadius="full" bg="green.400" mr={1}/>}
                            {renderActionIcons([dealer])}
                        </HStack>
                    ) : hasPermission(inactiveOrderReminderSendPermission)
                    && isHiddenDealer
                    && dealer.hiddenReason === "MANUAL" ? (
                        <IconWithTooltip
                            icon={FiEye}
                            fontSize="0.9rem"
                            color="green.500"
                            tooltipMessage="Include dealership in reminders"
                            onClick={() => includeDealership(dealer)}
                        />
                    ) : null}
                </Flex>
                <Divider my={2}/>
                <Flex justify="space-between" align="center">
                    <Text fontSize="12px" color="gray.600">{dealer.products?.length || 0} products</Text>
                    <Text
                        fontSize="12px"
                        color="blue.600"
                        cursor="pointer"
                        onClick={() => toggleExpand(dealer.dealershipId)}
                    >
                        {isExpanded ? "Hide products" : "Show products"}
                    </Text>
                </Flex>
                {isExpanded && (
                    <Stack mt={2} spacing={2}>
                        {dealer.products?.map((p) => mobileProductRow(dealer, p))}
                    </Stack>
                )}
            </Box>
        );
    });

    if (!hasPermission(inactiveOrderReminderViewPermission)) {
        return (
            <>
                <Breadcrumbs/>
                <Text mt={4} fontSize="13px">You do not have permission to view this page.</Text>
            </>
        );
    }

    return (
        <>
            <Breadcrumbs/>
            <Flex flexDir="column" height="100%" fontFamily="Poppins">
                <Flex
                    flexDir={isMobile ? "column" : "row"}
                    mb="10px"
                    justifyContent="space-between"
                    alignItems={isMobile ? "stretch" : "end"}
                    flexWrap={isMobile ? "wrap" : "nowrap"}
                    gap={3}
                >
                    <Flex flexDir="column" fontFamily="Poppins" mb={isMobile ? 2 : 0} gap={2}>
                        <RadioGroup
                            value={monthsThreshold ?? ""}
                            onChange={setMonthsThreshold}
                            isDisabled={monthsThreshold === null}
                        >
                            <HStack spacing={isMobile ? 1 : 4} flexWrap={isMobile ? "wrap" : "nowrap"}>
                                {monthOptions.map((o) => (
                                    <Radio key={o.value} value={o.value} size="sm">
                                        <Text fontSize={isMobile ? "11px" : "13px"} lineHeight="1.2" whiteSpace="nowrap">
                                            {o.label}
                                        </Text>
                                    </Radio>
                                ))}
                            </HStack>
                        </RadioGroup>
                        {pendingCount !== null && (
                            <Text fontSize={isMobile ? "12px" : "13px"} color="gray.700" lineHeight="short">
                                Dealerships to remind:{" "}
                                <Text as="span" fontWeight="semibold" color="blue.600">
                                    {pendingCount}
                                </Text>
                            </Text>
                        )}
                    </Flex>
                    <HStack
                        spacing={2}
                        alignItems="center"
                        ml={isMobile ? 0 : "auto"}
                        w={isMobile ? "100%" : undefined}
                        flexWrap="nowrap"
                    >
                        <Box
                            position="relative"
                            width={isMobile ? "auto" : "300px"}
                            flex={isMobile ? 1 : undefined}
                            minW={0}
                        >
                            <Input
                                placeholder="Dealership"
                                value={dealershipName}
                                onFocus={() => setIsDealershipDropdownOpen(true)}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setDealershipName(value);
                                    setSearchName(value);
                                    setCurrentPage(1);
                                    setIsDealershipDropdownOpen(true);
                                }}
                                onBlur={() => {
                                    setTimeout(() => setIsDealershipDropdownOpen(false), 120);
                                }}
                                pr={dealershipName ? "30px" : undefined}
                                size="sm"
                                fontSize="13px"
                                h="32px"
                                minH="32px"
                                borderRadius="md"
                                borderColor="gray.200"
                                bg="white"
                                _hover={{borderColor: "gray.300"}}
                                _focusVisible={{
                                    borderColor: "blue.400",
                                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
                                }}
                            />
                            {dealershipName && (
                                <IconButton
                                    aria-label="Clear dealership filter"
                                    size="xs"
                                    variant="ghost"
                                    icon={<CloseIcon boxSize={2}/>}
                                    position="absolute"
                                    right="4px"
                                    top="50%"
                                    transform="translateY(-50%)"
                                    onClick={() => {
                                        setDealershipName("");
                                        setSearchName("");
                                        setCurrentPage(1);
                                        setIsDealershipDropdownOpen(false);
                                    }}
                                />
                            )}
                            {isDealershipDropdownOpen && filteredDealershipSuggestions.length > 0 && (
                                <Box
                                    position="absolute"
                                    top="calc(100% + 4px)"
                                    left={0}
                                    right={0}
                                    zIndex={20}
                                    bg="white"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    borderRadius="md"
                                    boxShadow="md"
                                    maxH="50vh"
                                    overflowY="auto"
                                >
                                    {filteredDealershipSuggestions.map((name) => (
                                        <Box
                                            key={name}
                                            px={2}
                                            py={1.5}
                                            fontSize="xs"
                                            cursor="pointer"
                                            _hover={{bg: "gray.50"}}
                                            onMouseDown={() => {
                                                setDealershipName(name);
                                                setSearchName(name);
                                                setCurrentPage(1);
                                                setIsDealershipDropdownOpen(false);
                                            }}
                                        >
                                            {name}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                        <Select
                            size="sm"
                            w={{base: "150px", md: "190px"}}
                            flexShrink={0}
                            value={hiddenFilter}
                            onChange={(e) => {
                                setHiddenFilter(e.target.value as InactiveOrderReminderHiddenFilter);
                                setCurrentPage(1);
                            }}
                            fontSize="xs"
                            h="32px"
                            minH="32px"
                            borderRadius="md"
                            borderColor="gray.200"
                            bg="white"
                            title="Dealer visibility filter"
                        >
                            {HIDDEN_FILTER_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                        <AllowedAccess permissionKey={inactiveOrderReminderSendPermission}>
                            <IconWithTooltip
                                icon={FiSend}
                                tooltipMessage={sendReminderTooltip(
                                    selectedDealershipsWithSendableProducts.length > 0
                                        ? selectedDealershipsWithSendableProducts
                                        : selectedDealerships,
                                )}
                                onClick={() => openSendConfirm(selectedDealerships)}
                                isDisabled={
                                    selectedDealerships.length === 0
                                    || selectedDealershipsWithSendableProducts.length === 0
                                }
                            />
                        </AllowedAccess>
                    </HStack>
                </Flex>

                {isLoading || monthsThreshold === null ? (
                    <Loader/>
                ) : (
                    <>
                        {!isMobile && (
                            <InactiveReminderGridRow
                                variant="header"
                                cells={[
                                    <Text key="cb"> </Text>,
                                    <Text key="img">IMAGE</Text>,
                                    <Text key="name">DEALERSHIP / PRODUCT</Text>,
                                    <Text key="inactive">INACTIVE</Text>,
                                    <Text key="order">ORDER</Text>,
                                    <Text key="last">LAST ORDER/SEND</Text>,
                                    <Text key="actions">ACTIONS</Text>,
                                ]}
                            />
                        )}
                        {(isMobile ? mobileDealerCards : dealerRows).length > 0 ? (
                            isMobile ? mobileDealerCards : dealerRows
                        ) : (
                            <Text py={6} color="gray.600" fontSize="13px">
                                No dealerships with inactive products for the selected period.
                            </Text>
                        )}
                    </>
                )}

                {!isLoading && list.count > 0 && (
                    <Paginator
                        limit={list.count}
                        step={20}
                        current={currentPage}
                        swapPageTrigger={(p) => setCurrentPage(p)}
                    />
                )}
            </Flex>

            <InactiveOrderReminderRecipientsModal
                isOpen={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                dealerships={settingsDealers}
                onSaved={loadList}
            />

            <InactiveOrderReminderConfirmSendModal
                isOpen={confirmSendOpen}
                onClose={() => setConfirmSendOpen(false)}
                dealerships={sendDealers}
                monthsThreshold={Number(monthsThreshold ?? 6)}
                onSuccess={() => {
                    setSelectedIds(new Set());
                    loadList();
                }}
            />
        </>
    );
};

export default InactiveOrderReminder;
